/**
 * What a Save (or the server) answered (S8 Task 8.11): the path changed (412 `PathChanged` — its identity: reload the
 * path), its access control changed (`AclChanged`, or a background refetch found another ACL — reload the ACL; the diff
 * says what is on the server now), an unknown outcome, an authorization refusal with the operation's Owner sentence
 * (never for the account key — main sends none), or any other error. Save stays off until Reload.
 */
import { Button } from '../../../../../components/ui/button';
import type { StorageHnsAccessControl } from '../../../../../types/azureStorage';
import { AclChangePreview } from './AclChangePreview';
import type { AclEditorFeedback } from './aclEditorFeedback';

export const SERVER_CHANGED_TITLE = 'Changed on the server since it was loaded.';


type Props = { feedback: AclEditorFeedback; loaded: StorageHnsAccessControl; server?: StorageHnsAccessControl; dirty: boolean; onReload: () => void };

function reloadButton(props: Props) {
  return <Button type="button" variant="outline" size="xs" onClick={props.onReload} data-testid="hns-acl-reload">{props.dirty ? 'Discard changes and reload' : 'Reload'}</Button>;
}

export function AclSaveFeedback(props: Props) {
  const { feedback, loaded, server } = props;
  if (feedback.kind === 'refused' || feedback.kind === 'error') {
    const role = feedback.error.fix?.kind === 'needs-role' ? feedback.error.fix.role : undefined;
    return (
      <div className="grid gap-1 text-xs text-destructive" role="alert" data-testid="hns-acl-error">
        {role && <span className="w-fit rounded bg-destructive/10 px-1.5 py-0.5 font-medium" data-testid="hns-acl-needs-role">Needs {role}</span>}
        <span>{feedback.error.message}</span>
      </div>
    );
  }
  const title = feedback.kind === 'ambiguous' ? 'Outcome unknown.' : SERVER_CHANGED_TITLE;
  const detail = feedback.kind === 'server-changed' ? undefined : feedback.error.message;
  const showDiff = (feedback.kind === 'acl-changed' || feedback.kind === 'server-changed') && server;
  return (
    <div className="grid gap-2 text-xs" role="alert" data-testid={`hns-acl-${feedback.kind}`}>
      <span className="font-medium text-destructive">{title}</span>
      {detail && <span className="text-destructive">{detail}</span>}
      {showDiff && <AclChangePreview before={{ owner: loaded.owner, group: loaded.group, entries: loaded.entries }} after={{ owner: server.owner, group: server.group, entries: server.entries }} displayNames={server.displayNames} title="On the server now" testId="hns-acl-server-diff" />}
      <span className="text-muted-foreground">{props.dirty ? 'Your changes are kept below; saving stays off until you reload.' : 'Saving stays off until you reload.'}</span>
      <div>{reloadButton(props)}</div>
    </div>
  );
}
